import React, { useState } from 'react';
import { X, Loader2, ClipboardCheck, MessageSquare } from 'lucide-react';
import { Appointment, Question, QuestionnaireSubmission, QuestionnaireTemplate } from '@/types';
import { api } from '@/services/api';
import { ApiError } from '@/lib/apiError';
import { createId } from '@/lib/ids';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';

interface TechnicianQuestionnaireModalProps {
  appointment: Appointment;
  template: QuestionnaireTemplate;
  onSubmitted: (submission: QuestionnaireSubmission) => void;
  onClose: () => void;
}

const normalizeText = (value: string | undefined) => (value || '').trim();

// Same completeness rules as the customer portal: a required question must be
// answered, and a yes/no question that triggers details must have them filled.
const getQuestionError = (question: Question, answers: Record<string, string>): string | null => {
  const answer = normalizeText(answers[question.id]);
  if (question.required && !answer) return 'An answer is required.';
  const trigger = question.requireDetailsOn;
  if (trigger && trigger !== 'none' && answer === trigger) {
    if (!normalizeText(answers[`${question.id}_details`])) {
      return question.detailsLabel || 'Please provide further details.';
    }
  }
  return null;
};

/**
 * In-person questionnaire fill: the technician hands the device to the
 * candidate (or reads the questions out) and records the answers digitally at
 * the appointment — the counterpart of the customer portal's "Complete Now"
 * for candidates who arrive without having filled the form, e.g. the D&A
 * consent on a D&A-only booking.
 */
export const TechnicianQuestionnaireModal: React.FC<TechnicianQuestionnaireModalProps> = ({
  appointment,
  template,
  onSubmitted,
  onClose
}) => {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const questions = template.questions || [];
  const errorsByQuestion = new Map(
    questions.map((q) => [q.id, getQuestionError(q, answers)] as const)
  );
  const incompleteCount = [...errorsByQuestion.values()].filter(Boolean).length;

  const setAnswer = (key: string, value: string) =>
    setAnswers((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (incompleteCount > 0) {
      setShowErrors(true);
      return;
    }

    const consentNo = questions.some(
      (q) => q.type === 'yes_no' && q.consentWarning && normalizeText(answers[q.id]) === 'no'
    );
    if (consentNo) {
      const confirmed = await showConfirmModal(
        'The candidate has not given consent. We are unable to carry out the assessment without consent. Record this answer anyway?',
        { confirmText: 'Record without consent', cancelText: 'Go back', isDestructive: true }
      );
      if (!confirmed) return;
    }

    setIsSubmitting(true);
    const submission: QuestionnaireSubmission = {
      id: createId(),
      appointmentId: appointment.id,
      templateId: template.id,
      answers,
      submittedAt: new Date().toISOString()
    };
    try {
      await api.submitQuestionnaire(submission);
      onSubmitted(submission);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) {
        showAlertModal('This questionnaire has already been submitted for this appointment.');
        onClose();
      } else {
        showAlertModal(normalizeErrorMessage(err, 'Failed to submit questionnaire.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestion = (question: Question) => {
    const error = showErrors ? errorsByQuestion.get(question.id) : null;
    const detailsVisible =
      question.type === 'yes_no' &&
      question.requireDetailsOn &&
      question.requireDetailsOn !== 'none' &&
      normalizeText(answers[question.id]) === question.requireDetailsOn;

    return (
      <div
        key={question.id}
        className={`border rounded-2xl p-4 bg-slate-50/80 ${error ? 'border-red-300' : 'border-slate-100'}`}
      >
        {question.category && (
          <p className="text-[10px] font-black uppercase tracking-widest text-teal-600 mb-2">
            {question.category}
          </p>
        )}
        <label className="block font-black text-slate-800 mb-3 text-sm">
          {question.label} {question.required && <span className="text-red-500">*</span>}
        </label>

        {question.type === 'text' && (
          <input
            className="w-full border-2 border-slate-200 rounded-lg p-3 outline-none focus:border-teal-500 bg-white transition-all text-sm"
            value={answers[question.id] || ''}
            onChange={(e) => setAnswer(question.id, e.target.value)}
            disabled={isSubmitting}
          />
        )}
        {question.type === 'date' && (
          <input
            type="date"
            className="w-full border-2 border-slate-200 rounded-lg p-3 outline-none focus:border-teal-500 bg-white transition-all text-sm"
            value={answers[question.id] || ''}
            min="1900-01-01"
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setAnswer(question.id, e.target.value)}
            disabled={isSubmitting}
          />
        )}
        {question.type === 'textarea' && (
          <textarea
            className="w-full border-2 border-slate-200 rounded-lg p-3 outline-none focus:border-teal-500 bg-white transition-all text-sm"
            rows={3}
            value={answers[question.id] || ''}
            onChange={(e) => setAnswer(question.id, e.target.value)}
            disabled={isSubmitting}
          />
        )}
        {question.type === 'select' && (
          <select
            className="w-full border-2 border-slate-200 rounded-lg p-3 bg-white outline-none focus:border-teal-500 text-sm"
            value={answers[question.id] || ''}
            onChange={(e) => setAnswer(question.id, e.target.value)}
            disabled={isSubmitting}
          >
            <option value="">Select option...</option>
            {question.options?.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        )}
        {question.type === 'multi_select' && (
          <div className="space-y-2">
            {question.options?.map((opt) => {
              const selected = (answers[question.id] || '')
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean);
              const checked = selected.includes(opt);
              return (
                <label
                  key={opt}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    checked
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  } ${isSubmitting ? 'opacity-60 pointer-events-none' : ''}`}
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-teal-600 rounded accent-teal-600"
                    checked={checked}
                    disabled={isSubmitting}
                    onChange={() => {
                      const next = checked ? selected.filter((s) => s !== opt) : [...selected, opt];
                      setAnswer(question.id, next.join(', '));
                    }}
                  />
                  <span className="text-sm font-medium text-slate-700">{opt}</span>
                </label>
              );
            })}
          </div>
        )}
        {question.type === 'yes_no' && (
          <div className="space-y-3">
            {question.allowNa && (
              <p className="text-xs text-slate-500 italic">Please choose N/A if not applicable</p>
            )}
            <div className="flex gap-3">
              {(question.allowNa ? ['yes', 'no', 'n/a'] : ['yes', 'no']).map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setAnswer(question.id, opt)}
                  className={`flex-1 py-2.5 rounded-xl font-black text-xs uppercase border-2 transition-all shadow-sm ${
                    answers[question.id] === opt
                      ? opt === 'yes'
                        ? 'bg-teal-50 border-teal-500 text-teal-700'
                        : opt === 'no'
                          ? 'bg-slate-50 border-slate-500 text-slate-700'
                          : 'bg-orange-50 border-orange-400 text-orange-700'
                      : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'
                  }`}
                  disabled={isSubmitting}
                >
                  {opt.toUpperCase()}
                </button>
              ))}
            </div>
            {detailsVisible && (
              <div className="animate-fade-in-up bg-blue-50 border border-blue-100 p-4 rounded-xl">
                <label className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                  <MessageSquare className="w-3 h-3" />{' '}
                  {question.detailsLabel || 'Please provide further details'}
                </label>
                <textarea
                  className="w-full border-2 border-white rounded-lg p-3 text-sm focus:ring-4 focus:ring-blue-100 outline-none transition-all bg-white"
                  rows={2}
                  placeholder="Type here..."
                  value={answers[`${question.id}_details`] || ''}
                  onChange={(e) => setAnswer(`${question.id}_details`, e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            )}
          </div>
        )}

        {error && <p className="mt-2 text-xs font-medium text-red-600">{error}</p>}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[95] p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col animate-scale-up border border-slate-200 overflow-hidden">
        <div className="p-6 border-b bg-slate-50 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-teal-100 p-2.5 rounded-2xl text-teal-600">
              <ClipboardCheck className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-black text-lg text-slate-800 uppercase tracking-tight">
                {template.name}
              </h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                Complete with {appointment.customerName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-slate-400 hover:text-slate-700 bg-white p-2 rounded-full shadow-sm disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-4 custom-scrollbar">
          <div className="bg-blue-50 text-blue-800 text-xs p-3 rounded-xl">
            The candidate should answer these questions themselves. Reference:{' '}
            <strong>{appointment.id}</strong>
          </div>
          {questions.length === 0 ? (
            <p className="text-sm text-slate-500 italic text-center py-8">
              This questionnaire has no questions.
            </p>
          ) : (
            questions.map(renderQuestion)
          )}
        </div>

        <div className="p-6 border-t bg-slate-50 flex items-center justify-between gap-3 shrink-0">
          <p
            className={`text-[10px] font-bold uppercase tracking-widest ${incompleteCount === 0 ? 'text-green-600' : showErrors ? 'text-red-500' : 'text-slate-400'}`}
          >
            {incompleteCount === 0
              ? 'All questions answered'
              : `${incompleteCount} question${incompleteCount === 1 ? '' : 's'} remaining`}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="px-6 py-3 text-slate-400 font-bold uppercase text-[10px] tracking-widest"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || questions.length === 0}
              className="bg-teal-600 text-white px-8 py-3 rounded-2xl font-black hover:bg-teal-700 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed shadow-xl shadow-teal-600/20 active:scale-95 transition-all uppercase tracking-widest text-[11px]"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ClipboardCheck className="w-4 h-4" />
              )}
              Submit Questionnaire
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
